import { c as _c } from "react/compiler-runtime";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { BathIcon, BedIcon, ExpandIcon, Eye, Minus } from "lucide-react";
import Link from "next/link";
import { CloseButton } from "@/components/atoms/CloseButton";
import { BusinessTypeDot } from "@/components/atoms/business-type-dot/BusinessTypeDot";
import { RealEstateDetailWrapper } from "../../RealEstateDetailWrapper";
import { normalizeLocationName } from "@/utils/normalizeLocationName";
import { formatPrice } from "@/lib/price";
import CardFavorite from "@/components/cardElements/FavoritesModalGroup/CardFavorite";
import { ShareDrawer } from "../../RealEstateDetail/components/share-drawer";
import { cn } from "@/lib/utils";
import { useDetailDialog } from "./useDetailDialog";
import { ContextSwitcher } from "./ContextSwitcher";
import { FooterSkeleton } from "./FooterSkeleton";
import { Skeleton } from "@/components/ui/skeleton";
import { RoomSwitchOverlay } from "../../components/room-switch-overlay/RoomSwitchOverlay";
const MULTI_LABEL_KEYS = ["multipleDistricts", "multipleCounties", "multipleParishes"] as const;
type Props = {
  handleMaximizeDialog: () => void;
  minimizeDialog: (id: string | undefined) => void;
  handleOnOpenChange: (open: boolean) => void;
  handleDismissDialog: (id: string | undefined) => void;
};
export default function DialogModal(t0) {
  const $ = _c(78);
  const {
    handleMaximizeDialog,
    minimizeDialog,
    handleOnOpenChange,
    handleDismissDialog
  } = t0;
  const {
    t,
    tListingDetail,
    openModal,
    openModalId,
    realEstate,
    analytics,
    isFavorite,
    favRef,
    listingTitle,
    businessType,
    bedrooms,
    bathrooms,
    favoritesCount,
    viewCount,
    hasPrice,
    priceOnRequest,
    hasFooterBadges,
    breadcrumbParts,
    multiLocationsLevel,
    updatedAgoText,
    switcherItems,
    currentContextIndex,
    hasContextSwitcher,
    handleSelectContextIndex,
    houseName,
    isLoading,
    isSwitching
  } = useDetailDialog();
  let t1;
  let t2;
  if ($[0] !== handleDismissDialog || $[1] !== openModalId) {
    t1 = e => {
      e.preventDefault();
      handleDismissDialog(openModalId);
    };
    t2 = e_0 => {
      e_0.preventDefault();
      handleDismissDialog(openModalId);
    };
    $[0] = handleDismissDialog;
    $[1] = openModalId;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      width: "calc(100vw - 60px)",
      maxWidth: "1300px",
      height: "calc(100vh - 76px)"
    };
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== businessType || $[6] !== isLoading) {
    t4 = !isLoading && <BusinessTypeDot businessType={businessType} />;
    $[5] = businessType;
    $[6] = isLoading;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== houseName || $[9] !== isLoading || $[10] !== listingTitle || $[11] !== t) {
    t5 = isLoading ? <Skeleton className="h-4 w-48 bg-muted-strong" /> : houseName ? <><span className="text-primary">{houseName}</span>{listingTitle ? <span className="font-normal text-foreground-strong">{" "}· {listingTitle}</span> : null}</> : listingTitle ?? t("title");
    $[8] = houseName;
    $[9] = isLoading;
    $[10] = listingTitle;
    $[11] = t;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== t5) {
    t6 = <DialogTitle className="text-base font-semibold capitalize truncate pb-0 leading-tight text-foreground-strong" data-sentry-element="DialogTitle" data-sentry-source-file="DetailDialog.tsx">{t5}</DialogTitle>;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== t4 || $[16] !== t6) {
    t7 = <div className="flex items-center gap-3 min-w-0 mr-auto">{t4}{t6}</div>;
    $[15] = t4;
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <ExpandIcon className="h-4 w-4 text-foreground" data-sentry-element="ExpandIcon" data-sentry-source-file="DetailDialog.tsx" />;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== handleMaximizeDialog) {
    t9 = <div onClick={handleMaximizeDialog} className="p-1 cursor-pointer rounded-lg transition-colors hover:bg-muted">{t8}</div>;
    $[19] = handleMaximizeDialog;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== minimizeDialog || $[22] !== openModalId) {
    t10 = () => minimizeDialog(openModalId);
    $[21] = minimizeDialog;
    $[22] = openModalId;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <Minus className="h-4 w-4 text-foreground" data-sentry-element="Minus" data-sentry-source-file="DetailDialog.tsx" />;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== t10) {
    t12 = <div onClick={t10} className="p-1 cursor-pointer rounded-lg transition-colors hover:bg-muted">{t11}</div>;
    $[25] = t10;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <DialogClose asChild={true} data-sentry-element="DialogClose" data-sentry-source-file="DetailDialog.tsx"><CloseButton className="p-1 transition-colors hover:bg-muted" iconClassName="h-4 w-4 text-foreground" data-sentry-element="CloseButton" data-sentry-source-file="DetailDialog.tsx" /></DialogClose>;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== t12 || $[29] !== t7 || $[30] !== t9) {
    t14 = <div className="shrink-0 flex items-center gap-2 px-6 py-2.5">{t7}{t9}{t12}{t13}</div>;
    $[28] = t12;
    $[29] = t7;
    $[30] = t9;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== t) {
    t15 = t("title");
    $[32] = t;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] !== t15) {
    t16 = <DialogDescription className="sr-only" data-sentry-element="DialogDescription" data-sentry-source-file="DetailDialog.tsx">{t15}</DialogDescription>;
    $[34] = t15;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] !== openModalId) {
    t17 = <div className="h-full overflow-y-auto px-6 py-6 themed-scrollbar"><RealEstateDetailWrapper realEstateId={openModalId} inDialog={true} data-sentry-element="RealEstateDetailWrapper" data-sentry-source-file="DetailDialog.tsx" /></div>;
    $[36] = openModalId;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  let t18;
  if ($[38] !== isSwitching) {
    t18 = isSwitching && <RoomSwitchOverlay className="absolute inset-0 rounded-md" />;
    $[38] = isSwitching;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  let t19;
  if ($[40] !== t17 || $[41] !== t18) {
    t19 = <div className="relative flex-1 overflow-hidden mx-2 mb-2 rounded-md bg-linear-to-b from-primary/5 via-card to-background border border-border">{t17}{t18}</div>;
    $[40] = t17;
    $[41] = t18;
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  let t20;
  if ($[43] !== analytics || $[44] !== bathrooms || $[45] !== bedrooms || $[46] !== breadcrumbParts || $[47] !== businessType || $[48] !== currentContextIndex || $[49] !== favRef || $[50] !== favoritesCount || $[51] !== handleSelectContextIndex || $[52] !== hasContextSwitcher || $[53] !== hasFooterBadges || $[54] !== hasPrice || $[55] !== houseName || $[56] !== isFavorite || $[57] !== isLoading || $[58] !== multiLocationsLevel || $[59] !== priceOnRequest || $[60] !== realEstate || $[61] !== switcherItems || $[62] !== t || $[63] !== tListingDetail || $[64] !== updatedAgoText || $[65] !== viewCount) {
    t20 = isLoading ? <FooterSkeleton /> : hasFooterBadges && <div className="relative shrink-0 flex flex-col gap-1.5 px-6 pt-1.5 pb-3"><div className="flex items-center gap-2">{!!bedrooms && bedrooms > 0 ? <span className="shrink-0 flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-medium border border-border bg-muted text-foreground"><BedIcon className="w-4 h-4" />{bedrooms}</span> : null}{!!bathrooms && bathrooms > 0 ? <span className="shrink-0 flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-medium border border-border bg-muted text-foreground"><BathIcon className="w-4 h-4" />{bathrooms}</span> : null}<div className="flex items-center gap-2 ml-auto">{analytics && realEstate && <><span onClick={() => favRef.current?.click()} className="shrink-0 flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-medium hover:bg-card/25 cursor-pointer border border-border bg-muted text-foreground"><CardFavorite ref={favRef} fill="white" className={cn("w-4 h-4 flex items-center cursor-pointer", isFavorite ? "text-red-500" : "text-muted-foreground")} realEstateSlug={realEstate.slug} realEstateId={realEstate.id} favorite={isFavorite} /><span>{favoritesCount}</span></span><span className="shrink-0 flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-medium hover:bg-card/25 cursor-pointer border border-border bg-muted text-foreground"><ShareDrawer realEstateId={realEstate.slug} className="w-4 h-4 flex items-center cursor-pointer text-muted-foreground" fill="white" title={realEstate.title} /></span><span className="shrink-0 flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-medium border border-border bg-muted text-foreground"><Eye className="w-4 h-4" />{viewCount}</span></>}<p className="text-xl font-bold whitespace-nowrap ml-3 text-foreground-strong">{hasPrice ? <>{realEstate?.rentPrice ? formatPrice(realEstate.rentPrice) : realEstate?.salePrice ? formatPrice(realEstate.salePrice) : realEstate?.lastBidPrice ? formatPrice(realEstate.lastBidPrice) : realEstate?.minPrice ? formatPrice(realEstate.minPrice) : ""}{(businessType === "roomRent" || businessType === "rent") && <span className="text-sm font-medium ml-1 text-muted-foreground">/{tListingDetail("perMonth")}</span>}</> : priceOnRequest}</p></div></div>{(breadcrumbParts.length > 0 || multiLocationsLevel !== null || updatedAgoText) && <div className="flex items-center justify-between gap-3 text-[11px] text-muted-foreground">{breadcrumbParts.length > 0 || multiLocationsLevel !== null ? <div className="flex items-center gap-1 truncate">{breadcrumbParts.map((part, i) => {
            const href = `/${part.level}/${normalizeLocationName(part.label)}/${businessType ?? ""}`;
            return <span key={`${part.code}-${i}`} className="flex items-center gap-1 truncate">{i > 0 && <span className="opacity-50">›</span>}<Link href={href} className="capitalize truncate hover:underline underline-offset-2">{part.label}</Link></span>;
          })}{multiLocationsLevel !== null && <span className="flex items-center gap-1 truncate">{breadcrumbParts.length > 0 && <span className="opacity-50">›</span>}<span className="truncate">{t(MULTI_LABEL_KEYS[multiLocationsLevel])}</span></span>}</div> : <span />}{updatedAgoText && <span className="shrink-0">{updatedAgoText}</span>}</div>}{hasContextSwitcher && <ContextSwitcher switcherItems={switcherItems} currentContextIndex={currentContextIndex} houseName={houseName} onSelect={handleSelectContextIndex} />}</div>;
    $[43] = analytics;
    $[44] = bathrooms;
    $[45] = bedrooms;
    $[46] = breadcrumbParts;
    $[47] = businessType;
    $[48] = currentContextIndex;
    $[49] = favRef;
    $[50] = favoritesCount;
    $[51] = handleSelectContextIndex;
    $[52] = hasContextSwitcher;
    $[53] = hasFooterBadges;
    $[54] = hasPrice;
    $[55] = houseName;
    $[56] = isFavorite;
    $[57] = isLoading;
    $[58] = multiLocationsLevel;
    $[59] = priceOnRequest;
    $[60] = realEstate;
    $[61] = switcherItems;
    $[62] = t;
    $[63] = tListingDetail;
    $[64] = updatedAgoText;
    $[65] = viewCount;
    $[66] = t20;
  } else {
    t20 = $[66];
  }
  let t21;
  if ($[67] !== t1 || $[68] !== t14 || $[69] !== t16 || $[70] !== t19 || $[71] !== t2 || $[72] !== t20) {
    t21 = <DialogContent hasClose={false} onInteractOutside={t1} onEscapeKeyDown={t2} style={t3} className="p-0 overflow-hidden flex flex-col top-[calc(50%+8px)] bg-background border border-border shadow-none" data-sentry-element="DialogContent" data-sentry-source-file="DetailDialog.tsx">{t14}{t16}{t19}{t20}</DialogContent>;
    $[67] = t1;
    $[68] = t14;
    $[69] = t16;
    $[70] = t19;
    $[71] = t2;
    $[72] = t20;
    $[73] = t21;
  } else {
    t21 = $[73];
  }
  let t22;
  if ($[74] !== handleOnOpenChange || $[75] !== openModal || $[76] !== t21) {
    t22 = <Dialog open={openModal} onOpenChange={handleOnOpenChange} data-sentry-element="Dialog" data-sentry-component="DialogModal" data-sentry-source-file="DetailDialog.tsx">{t21}</Dialog>;
    $[74] = handleOnOpenChange;
    $[75] = openModal;
    $[76] = t21;
    $[77] = t22;
  } else {
    t22 = $[77];
  }
  return t22;
}